import { NestedKeyOf } from '../types/field-path.type';
import { DataSourceMode, GridDensity, MobileMode, SelectAllMode, SelectionMode } from '../types/data-source.type';
import { SortState } from './sort-state.interface';
import { PaginationConfig } from './pagination-state.interface';
import { ContextMenuItem } from './context-menu.interface';
import { FooterConfig } from './footer.interface';

/**
 * Main grid configuration interface
 * Controls all aspects of grid behavior and appearance
 *
 * @template T - The type of data in the grid
 */
export interface GridConfig<T> {
  // ============================================================================
  // DATA SOURCE
  // ============================================================================

  /**
   * Data mode for the grid
   * - 'local': All data provided upfront, operations happen client-side
   * - 'api': Operations emit events for external API handling
   * @default 'local'
   */
  dataMode?: DataSourceMode;

  /**
   * Unique ID field for row identity (required for selection, expansion, etc.)
   * @default '_id'
   * @example '_id'
   * @example 'serial'
   */
  rowIdField?: keyof T;

  // ============================================================================
  // SORTING
  // ============================================================================

  /**
   * Sorting configuration
   */
  sorting?: {
    /**
     * Enable sorting
     * @default true
     */
    enabled?: boolean;

    /**
     * Sorting mode
     * @default 'local'
     */
    mode?: DataSourceMode;

    /**
     * Enable multi-column sorting
     * @default true
     */
    multiColumn?: boolean;

    /**
     * Default/initial sort state
     * @example [{ field: 'createdAt', direction: 'desc' }]
     */
    defaultSort?: SortState<T>[];

    /**
     * Maximum number of columns that can be sorted simultaneously
     * Only applies when multiColumn is true
     * @default 3
     */
    maxMultiSortColumns?: number;
  };

  // ============================================================================
  // PAGINATION
  // ============================================================================

  /**
   * Pagination configuration
   * Uses PaginationConfig interface
   */
  pagination?: PaginationConfig;

  // ============================================================================
  // SEARCH
  // ============================================================================

  /**
   * Global search configuration
   */
  search?: {
    /**
     * Enable global search
     * @default false
     */
    enabled?: boolean;

    /**
     * Search mode
     * @default 'local'
     */
    mode?: DataSourceMode;

    /**
     * Show built-in search input
     * Set to false if you want to use an external search input
     * @default true
     */
    showSearchInput?: boolean;

    /**
     * Debounce time in milliseconds
     * @default 300
     */
    debounceTime?: number;

    /**
     * Placeholder text for search input
     * @default 'Buscar...'
     */
    placeholder?: string;

    /**
     * Fields to search in (local mode only)
     * If not specified, searches all visible columns
     */
    searchableFields?: NestedKeyOf<T>[];

    /**
     * Case sensitive search
     * @default false
     */
    caseSensitive?: boolean;

    /**
     * Position of search input
     * @default 'top'
     */
    searchPosition?: 'top' | 'bottom' | 'both';
  };

  // ============================================================================
  // COLUMN FILTERS
  // ============================================================================

  /**
   * Column filtering configuration (Excel-like filters)
   */
  columnFilters?: {
    /**
     * Enable column filters
     * @default true
     */
    enabled?: boolean;

    /**
     * Default mode for all columns
     * Individual columns can override via ColDef.filterOptions
     * @default 'local'
     */
    mode?: DataSourceMode;

    /**
     * Show filter icon in header
     * @default true
     */
    showFilterIcon?: boolean;

    /**
     * Filter menu trigger
     * @default 'click'
     */
    trigger?: 'click' | 'hover';
  };

  // ============================================================================
  // SELECTION
  // ============================================================================

  /**
   * Row selection configuration
   */
  selection?: {
    /**
     * Enable row selection
     * @default false
     */
    enabled?: boolean;

    /**
     * Selection mode
     * @default 'multiple'
     */
    mode?: SelectionMode;

    /**
     * Select all mode (for multiple selection)
     * - 'page': Select all rows in current page
     * - 'all': Select all rows (use carefully in API mode)
     * @default 'page'
     */
    selectAllMode?: SelectAllMode;

    /**
     * Show checkbox column
     * @default true
     */
    showCheckboxColumn?: boolean;

    /**
     * Checkbox column configuration
     */
    checkboxColumn?: {
      /**
       * Pin checkbox column
       * @default 'left'
       */
      pinned?: 'left' | 'right';

      /**
       * Column width
       * @default 50
       */
      width?: number;

      /**
       * Header name
       * @default ''
       */
      headerName?: string;
    };

    /**
     * Enable row selection on click (anywhere in row, not just checkbox)
     * @default false
     */
    selectOnRowClick?: boolean;

    /**
     * Deselect on clicking selected row
     * @default true
     */
    deselectOnClickSelected?: boolean;

    /**
     * Remember selection across pagination
     * @default true
     */
    rememberSelection?: boolean;

    /**
     * Function to determine if a row is selectable
     * @param row - The row data
     * @returns true if the row should be selectable, false to disable the checkbox
     * @example
     * isRowSelectable: (row) => row.status !== 'completed'
     */
    isRowSelectable?: (row: any) => boolean;

    /**
     * Field name containing the selectable state
     * If specified, the grid will look for this field in the row data
     * to determine if the row is selectable
     * @example 'isSelectable'
     */
    selectableField?: string;
  };

  // ============================================================================
  // ROW EXPANSION
  // ============================================================================

  /**
   * Row expansion configuration
   */
  rowExpansion?: {
    /**
     * Enable row expansion
     * @default false
     */
    enabled?: boolean;

    /**
     * Expand row on click (anywhere in row)
     * @default false
     */
    expandOnClick?: boolean;

    /**
     * Allow multiple rows expanded simultaneously
     * @default true
     */
    multipleExpand?: boolean;

    /**
     * Expand icon column configuration
     */
    expandColumn?: {
      /**
       * Pin expand column
       * @default 'left'
       */
      pinned?: 'left' | 'right';

      /**
       * Column width
       * @default 50
       */
      width?: number;
    };

    /**
     * Animation duration in milliseconds
     * @default 225
     */
    animationDuration?: number;
  };

  // ============================================================================
  // COLUMN VISIBILITY
  // ============================================================================

  /**
   * Column visibility panel configuration
   * @deprecated Use settingsPanel instead for integrated settings experience
   */
  columnVisibility?: {
    /**
     * Enable column visibility control
     * @default true
     */
    enabled?: boolean;

    /**
     * Show toggle button for visibility panel
     * @default true
     */
    showToggleButton?: boolean;

    /**
     * Panel position
     * @default 'right'
     */
    panelPosition?: 'left' | 'right';

    /**
     * Panel title
     * @default 'Columnas'
     */
    panelTitle?: string;

    /**
     * Allow search within columns list
     * @default true
     */
    searchable?: boolean;
  };

  // ============================================================================
  // SETTINGS PANEL (NEW - Replaces individual toolbars)
  // ============================================================================

  /**
   * Unified settings panel configuration
   * Lateral sliding panel with book-tab design for all grid configurations
   * Provides a centralized location for:
   * - Column visibility
   * - Grid density
   * - Export options
   * - Future settings (themes, filters, etc.)
   */
  settingsPanel?: {
    /**
     * Enable settings panel
     * When enabled, replaces the old toolbar approach
     * @default true
     */
    enabled?: boolean;

    /**
     * Panel position (side of the grid)
     * @default 'right'
     */
    position?: 'left' | 'right';

    /**
     * Enable column visibility section
     * @default true
     */
    enableColumnVisibility?: boolean;

    /**
     * Enable grid density section
     * Allows users to switch between compact/comfortable/spacious modes
     * @default true
     */
    enableDensity?: boolean;

    /**
     * Enable export section
     * Provides quick CSV/Excel export buttons
     * @default true
     */
    enableExport?: boolean;

    /**
     * Default grid density
     * @default 'comfortable'
     */
    defaultDensity?: 'compact' | 'comfortable' | 'spacious';
  };

  // ============================================================================
  // COLUMN PINNING
  // ============================================================================

  /**
   * Column pinning configuration
   */
  columnPinning?: {
    /**
     * Enable column pinning
     * @default true
     */
    enabled?: boolean;

    /**
     * Allow user to pin/unpin columns via UI
     * @default false (only programmatic pinning via ColDef)
     */
    allowUserPinning?: boolean;
  };

  // ============================================================================
  // TREE DATA
  // ============================================================================

  /**
   * Tree grid configuration
   */
  treeData?: {
    /**
     * Enable tree grid mode
     * @default false
     */
    enabled?: boolean;

    /**
     * Field containing children
     * @example 'children'
     */
    childrenField?: keyof T;

    /**
     * Auto-expand depth
     * -1 = expand all, 0 = collapse all, n = expand n levels
     * @default 0
     */
    expandDepth?: number;

    /**
     * Show expand/collapse controls
     * @default true
     */
    showExpandControls?: boolean;

    /**
     * Indent size per level (pixels)
     * @default 20
     */
    indentSize?: number;
  };

  // ============================================================================
  // GROUPING
  // ============================================================================

  /**
   * Row grouping configuration
   */
  grouping?: {
    /**
     * Enable row grouping
     * @default false
     */
    enabled?: boolean;

    /**
     * Default group by fields
     */
    defaultGroupBy?: NestedKeyOf<T>[];

    /**
     * Show group panel for drag & drop grouping
     * @default false
     */
    showGroupPanel?: boolean;

    /**
     * Allow ungrouping
     * @default true
     */
    allowUngroup?: boolean;

    /**
     * Indent size per level (pixels)
     * @default 20
     */
    indentSize?: number;

    /**
     * Show aggregate values in group rows
     * @default true
     */
    showAggregates?: boolean;

    /**
     * Field labels map for display in group panel
     * @example { 'warehouse.name': 'Almacén', 'status': 'Estado' }
     */
    fieldLabels?: Record<string, string>;

    /**
     * Panel placeholder text
     * @default 'Arrastra columnas aquí para agrupar'
     */
    panelText?: string;
  };

  // ============================================================================
  // CONTEXT MENU
  // ============================================================================

  /**
   * Context menu configuration
   * RIGHT-CLICK MENU - OPT-IN FEATURE (disabled by default)
   */
  contextMenu?: {
    /**
     * Enable context menu on right-click
     * ⚠️ IMPORTANT: This is an OPT-IN feature (disabled by default)
     * The context menu will ONLY work when explicitly enabled by the developer
     * When disabled, native browser context menu works normally
     * @default false
     */
    enabled?: boolean;

    /**
     * Context menu items
     * Define the menu items to show when right-clicking a row
     * @example
     * ```typescript
     * items: [
     *   {
     *     label: 'Edit',
     *     icon: 'edit',
     *     action: (row) => this.editRow(row),
     *     disabled: (row) => row.statusId === 0
     *   },
     *   { divider: true },
     *   {
     *     label: 'Delete',
     *     icon: 'delete',
     *     action: (row) => this.deleteRow(row),
     *     color: 'warn'
     *   }
     * ]
     * ```
     */
    items?: ContextMenuItem<T>[];

    /**
     * Prevent default browser context menu
     * @default true
     */
    preventDefault?: boolean;

    /**
     * Stop event propagation
     * @default true
     */
    stopPropagation?: boolean;
  };

  // ============================================================================
  // STATE PERSISTENCE
  // ============================================================================

  /**
   * State persistence configuration
   * Saves grid state to localStorage/sessionStorage
   */
  statePersistence?: {
    /**
     * Enable state persistence
     * @default false
     */
    enabled?: boolean;

    /**
     * Storage key prefix
     * @default 'smart-grid-state'
     */
    storageKey?: string;

    /**
     * Storage type
     * @default 'localStorage'
     */
    storageType?: 'localStorage' | 'sessionStorage';

    /**
     * Features to persist
     * Fine-grained control over what gets saved
     */
    features?: {
      /**
       * Save column visibility
       * @default true
       */
      columnVisibility?: boolean;

      /**
       * Save column order
       * @default false
       */
      columnOrder?: boolean;

      /**
       * Save column pinning
       * @default true
       */
      columnPinning?: boolean;

      /**
       * Save column widths
       * @default false
       */
      columnWidths?: boolean;

      /**
       * Save sort state
       * @default false
       */
      sorting?: boolean;

      /**
       * Save filter state
       * @default false
       */
      filters?: boolean;

      /**
       * Save pagination state
       * @default false
       */
      pagination?: boolean;

      /**
       * Save group by state
       * @default false
       */
      grouping?: boolean;
    };
  };

  // ============================================================================
  // RESPONSIVE
  // ============================================================================

  /**
   * Responsive behavior configuration
   */
  responsive?: {
    /**
     * Enable responsive mode
     * @default true
     */
    enabled?: boolean;

    /**
     * Breakpoints in pixels
     */
    breakpoints?: {
      /**
       * Mobile breakpoint
       * @default 640
       */
      mobile?: number;

      /**
       * Tablet breakpoint
       * @default 1024
       */
      tablet?: number;
    };

    /**
     * Mobile display mode
     * - 'cards': Show data as cards
     * - 'compact': Show compact table
     * - 'table': Show normal table (scrollable)
     * @default 'compact'
     */
    mobileMode?: MobileMode;

    /**
     * Hide columns based on responsive priority in ColDef
     * @default true
     */
    autoHideColumns?: boolean;

    /**
     * Specific columns to hide on mobile
     * Overrides autoHideColumns if specified
     */
    hideColumnsOnMobile?: NestedKeyOf<T>[];

    /**
     * Specific columns to hide on tablet
     * Overrides autoHideColumns if specified
     */
    hideColumnsOnTablet?: NestedKeyOf<T>[];

    /**
     * Critical columns that should never be hidden
     * Always visible regardless of viewport
     */
    criticalColumns?: NestedKeyOf<T>[];

    /**
     * Stack multiple column values in one column on mobile
     * Shows multiple fields vertically in a single table column
     * @default false
     */
    stackColumnsOnMobile?: boolean;

    /**
     * Enable compact mode on tablet
     * Reduces padding, font sizes for more content
     * @default true
     */
    compactModeOnTablet?: boolean;

    /**
     * Enable touch-optimized controls
     * Larger touch targets, swipe gestures
     * @default true
     */
    touchOptimized?: boolean;

    /**
     * Minimum touch target size (pixels)
     * Applies to buttons, checkboxes, etc.
     * @default 44
     */
    minTouchTargetSize?: number;

    /**
     * Enable swipe gestures for actions on mobile
     * @default false
     */
    enableSwipeGestures?: boolean;
  };

  // ============================================================================
  // KEYBOARD NAVIGATION
  // ============================================================================

  /**
   * Keyboard navigation configuration
   */
  keyboardNavigation?: {
    /**
     * Enable keyboard navigation
     * @default true
     */
    enabled?: boolean;

    /**
     * Enable Enter key to open/edit
     * @default true
     */
    enterKeyAction?: 'edit' | 'expand' | 'select' | 'none';

    /**
     * Enable Tab navigation between cells
     * @default true
     */
    tabNavigation?: boolean;
  };

  // ============================================================================
  // AUTO-SIZING
  // ============================================================================

  /**
   * Auto-sizing configuration
   * Controls programmatic column sizing via API
   */
  autoSizing?: {
    /**
     * Enable auto-sizing
     * @default true
     */
    enabled?: boolean;

    /**
     * Include header text in size calculation
     * @default true
     */
    includeHeaders?: boolean;

    /**
     * Skip hidden columns
     * @default true
     */
    skipHidden?: boolean;

    /**
     * Padding to add to calculated width
     * @default 20
     */
    padding?: number;
  };

  // ============================================================================
  // STYLING
  // ============================================================================

  /**
   * Styling and appearance configuration
   */
  styling?: {
    /**
     * Grid density
     * @default 'comfortable'
     */
    density?: GridDensity;

    /**
     * Container height (CSS value)
     * Sets a fixed height for the grid container
     * @example '500px', '50vh', 'calc(100vh - 200px)'
     */
    height?: string;

    /**
     * Container max-height (CSS value)
     * Limits the maximum height, useful for preventing excessive scrolling
     * @example '600px', '80vh'
     * @default '70vh'
     */
    maxHeight?: string;

    /**
     * Container min-height (CSS value)
     * Sets a minimum height for the grid
     * @example '200px', '30vh'
     */
    minHeight?: string;

    /**
     * Row height in pixels (overrides density)
     */
    rowHeight?: number;

    /**
     * Header height in pixels
     * @default 56
     */
    headerHeight?: number;

    /**
     * Show borders
     * @default true
     */
    showBorders?: boolean;

    /**
     * Striped rows (alternating background)
     * @default false
     */
    stripedRows?: boolean;

    /**
     * Hover effect on rows
     * @default true
     */
    rowHover?: boolean;

    /**
     * Custom CSS class for grid container
     */
    gridClass?: string | string[];

    /**
     * Custom CSS class for table
     */
    tableClass?: string | string[];

    /**
     * Cell padding horizontal (overrides density)
     * CSS value for horizontal padding
     * @example '16px', '1rem', '20px'
     * @default '16px'
     */
    cellPaddingX?: string;

    /**
     * Cell padding vertical (overrides density)
     * CSS value for vertical padding
     * @example '12px', '0.75rem', '8px'
     * @default '12px'
     */
    cellPaddingY?: string;

    /**
     * Font size for table cells and headers
     * CSS value for font-size
     * @example '12px', '14px', '0.875rem'
     * @default '12px'
     */
    fontSize?: string;
  };

  // ============================================================================
  // PERFORMANCE
  // ============================================================================

  /**
   * Performance optimization configuration
   */
  performance?: {
    /**
     * Enable virtual scrolling for large datasets
     * Uses Angular CDK Virtual Scrolling to render only visible rows
     * @default false
     */
    virtualScrolling?: boolean;

    /**
     * Virtual scroll item size (row height in pixels)
     * Required when virtualScrolling is enabled
     * @default 48
     */
    virtualScrollItemSize?: number;

    /**
     * Virtual scroll buffer size (number of items to render outside viewport)
     * @default 10
     */
    virtualScrollBufferSize?: number;

    /**
     * Batch size for rendering
     * @default 50
     */
    batchSize?: number;

    /**
     * Debounce time for filter/search (ms)
     * @default 300
     */
    debounceTime?: number;
  };

  // ============================================================================
  // MISCELLANEOUS
  // ============================================================================

  /**
   * Loading state configuration
   */
  loading?: {
    /**
     * Show loading overlay
     * @default true
     */
    showOverlay?: boolean;

    /**
     * Loading text
     * @default 'Cargando...'
     */
    loadingText?: string;
  };

  /**
   * Empty state configuration
   */
  emptyState?: {
    /**
     * Message to show when no data
     * @default 'No hay registros para mostrar'
     */
    message?: string;

    /**
     * Show icon in empty state
     * @default true
     */
    showIcon?: boolean;

    /**
     * Icon name (Material icon)
     * @default 'inbox'
     */
    icon?: string;
  };

  /**
   * Accessibility configuration
   */
  accessibility?: {
    /**
     * Enable ARIA labels
     * @default true
     */
    enableAriaLabels?: boolean;

    /**
     * Announce sort changes to screen readers
     * @default true
     */
    announceSortChanges?: boolean;

    /**
     * Announce filter changes to screen readers
     * @default true
     */
    announceFilterChanges?: boolean;
  };

  /**
   * Debug mode (shows console logs)
   * @default false
   */
  debug?: boolean;

  // ============================================================================
  // FOOTER
  // ============================================================================

  /**
   * Footer row configuration
   * Enables sticky footer with aggregation functions
   * Configure individual column footers via ColDef.footer
   */
  footer?: FooterConfig;
}

/**
 * Default grid configuration
 * Used as fallback when user doesn't provide specific config
 */
export const DEFAULT_GRID_CONFIG: GridConfig<any> = {
  dataMode: 'local',
  rowIdField: '_id' as any,

  sorting: {
    enabled: true,
    mode: 'local',
    multiColumn: true,
    maxMultiSortColumns: 3,
  },

  pagination: {
    enabled: true,
    mode: 'local',
    pageSize: 50,
    pageSizeOptions: [50, 100, 150, 200],
    showPaginator: true,
    showFirstLastButtons: true,
    position: 'bottom',
  },

  search: {
    enabled: false,
    mode: 'local',
    showSearchInput: true,
    debounceTime: 300,
    placeholder: 'Buscar...',
    caseSensitive: false,
    searchPosition: 'top',
  },

  columnFilters: {
    enabled: true,
    mode: 'local',
    showFilterIcon: true,
    trigger: 'click',
  },

  selection: {
    enabled: false,
    mode: 'multiple',
    selectAllMode: 'page',
    showCheckboxColumn: true,
    checkboxColumn: {
      pinned: 'left',
      width: 50,
      headerName: '',
    },
    selectOnRowClick: false,
    deselectOnClickSelected: true,
    rememberSelection: true,
  },

  rowExpansion: {
    enabled: false,
    expandOnClick: false,
    multipleExpand: true,
    expandColumn: {
      pinned: 'left',
      width: 50,
    },
    animationDuration: 225,
  },

  columnVisibility: {
    enabled: true,
    showToggleButton: true,
    panelPosition: 'right',
    panelTitle: 'Columnas',
    searchable: true,
  },

  settingsPanel: {
    enabled: false,
    position: 'right',
    enableColumnVisibility: false,
    enableDensity: false,
    enableExport: false,
    defaultDensity: 'comfortable',
  },

  columnPinning: {
    enabled: true,
    allowUserPinning: false,
  },

  treeData: {
    enabled: false,
    expandDepth: 0,
    showExpandControls: true,
    indentSize: 20,
  },

  grouping: {
    enabled: false,
    allowUngroup: true,
    showGroupPanel: false,
    indentSize: 20,
    showAggregates: true,
    panelText: 'Arrastra columnas aquí para agrupar',
  },

  contextMenu: {
    enabled: false,  // ❌ DISABLED BY DEFAULT - OPT-IN ONLY
    preventDefault: true,
    stopPropagation: true,
  },

  statePersistence: {
    enabled: false,
    storageKey: 'smart-grid-state',
    storageType: 'localStorage',
    features: {
      columnVisibility: true,
      columnOrder: false,
      columnPinning: true,
      columnWidths: false,
      sorting: false,
      filters: false,
      pagination: false,
      grouping: false,
    },
  },

  responsive: {
    enabled: true,
    breakpoints: {
      mobile: 640,
      tablet: 1024,
    },
    mobileMode: 'compact',
    autoHideColumns: true,
    stackColumnsOnMobile: false,
    compactModeOnTablet: true,
    touchOptimized: true,
    minTouchTargetSize: 44,
    enableSwipeGestures: false,
  },

  keyboardNavigation: {
    enabled: true,
    enterKeyAction: 'none',
    tabNavigation: true,
  },

  autoSizing: {
    enabled: true,
    includeHeaders: true,
    skipHidden: true,
    padding: 20,
  },

  styling: {
    density: 'comfortable',
    maxHeight: '70vh',
    headerHeight: 56,
    showBorders: true,
    stripedRows: false,
    rowHover: true,
    cellPaddingX: '3px',
    cellPaddingY: '0px',
    fontSize: '12px',
  },

  performance: {
    virtualScrolling: false,
    virtualScrollItemSize: 48,
    virtualScrollBufferSize: 10,
    batchSize: 50,
    debounceTime: 300,
  },

  loading: {
    showOverlay: true,
    loadingText: 'Cargando...',
  },

  emptyState: {
    message: 'No hay registros para mostrar',
    showIcon: true,
    icon: 'inbox',
  },

  accessibility: {
    enableAriaLabels: true,
    announceSortChanges: true,
    announceFilterChanges: true,
  },

  debug: false,

  footer: {
    enabled: false,
    sticky: true,
    height: '48px',
  },
};
